import { useApolloClient } from '@apollo/client';
import { Form, message } from 'antd';
import { Formik } from 'formik';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type AudiSalesForceModuleSpecsFragment } from '../../../../api/fragments/AudiSalesForceModuleSpecs';
import {
    UpdateAudiSalesForceModuleDocument,
    type UpdateAudiSalesForceModuleMutation,
    type UpdateAudiSalesForceModuleMutationVariables,
} from '../../../../api/mutations/updateAudiSalesForceModule';
import { type AudiSalesForceModuleSettings } from '../../../../api/types';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';
import AudiSalesForceModuleUpdateForm from './AudiSalesForceModuleUpdateForm';

type AudiSalesForceModuleMainDetailsProps = {
    module: AudiSalesForceModuleSpecsFragment;
};

const validator = validators.compose(
    validators.requiredNonEmptyString('displayName'),
    validators.requiredString('setting.tradeInModuleId'),
    validators.requiredString('setting.baseUrl'),
    validators.requiredString('setting.clientId'),
    validators.requiredString('setting.clientSecret')
);

const AudiSalesForceModuleMainDetails = ({ module }: AudiSalesForceModuleMainDetailsProps) => {
    const { t } = useTranslation(['audiSalesForceModuleDetails']);

    const apolloClient = useApolloClient();

    const { setting } = module;

    const initialValues = useMemo<AudiSalesForceModuleSettings>(
        () => ({
            displayName: module.displayName,
            setting: {
                tradeInModuleId: module.tradeInModuleId,
                baseUrl: setting?.baseUrl,
                clientId: setting?.clientId,
                clientSecret: setting?.clientSecret,
                authority: setting?.authority,
                publicCert: setting?.publicCert,
            },
        }),
        [module.displayName, module.tradeInModuleId, setting]
    );

    const onSubmit = useHandleError(
        async (values: AudiSalesForceModuleSettings) => {
            if (!values.displayName) {
                return;
            }

            message.loading({
                content: t('audiSalesForceModuleDetails:messages.updateMainDetailsSubmitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<UpdateAudiSalesForceModuleMutation, UpdateAudiSalesForceModuleMutationVariables>({
                    mutation: UpdateAudiSalesForceModuleDocument,
                    variables: {
                        moduleId: module.id,
                        settings: values,
                    },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('audiSalesForceModuleDetails:messages.updateMainDetailsSuccessful'),
                key: 'primary',
            });
        },
        [apolloClient, module.id, t]
    );

    const validate = useValidator(validator);

    return (
        <Formik<AudiSalesForceModuleSettings> initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form
                    id="updateAudiSalesForceModuleForm"
                    layout="vertical"
                    name="updateAudiSalesForceModuleForm"
                    onSubmitCapture={handleSubmit}
                >
                    <AudiSalesForceModuleUpdateForm module={module} />
                </Form>
            )}
        </Formik>
    );
};

export default AudiSalesForceModuleMainDetails;
